import {Box, Divider, ListItemText, Skeleton, Stack, Typography, useTheme} from '@mui/material';
import React, {Fragment, MouseEvent} from 'react';

import {
	BeneficiaryModel,
	BeneficiarySituationEnum,
} from 'domain/usecases/others/selection-flow/beneficiary-v3';
import {SelectionUserPlanEnum} from 'main/analytics/gaTagsEnum';
import Icon from 'presentation/icons';
import {planTypeOptions} from 'presentation/pages';
import {translations} from 'presentation/translations-new';
import {formatText} from 'presentation/utils';

import {TagChip} from 'presentation/components';
import {AlertSuspended} from 'presentation/components/AlertSuspended';
import {Loading} from 'presentation/components/Loading/Loading';
import {FeaturesEnum, PlanTypeEnum, UserTypeEnum} from 'presentation/constants';
import {useGetBeneficiaries} from '../../queries/useGetBeneficiaries';
import {StyledButton, StyledCard} from './styles';
import {PlanCardProps} from './types';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {AlertCancelled} from 'presentation/components/AlertCancelled';

const {cancelIn, cancelContract} = translations['pt-br'].commons;
const {
	contactNumber,
	beneficiaries,
	select,
	selected: selectedLabel,
} = translations['pt-br'].others.selectionFlow;

export function PlanCard({selected, onSelect, plan, beneficiaryName, actionButton}: PlanCardProps) {
	const {
		nmPlano,
		tipoPlanoC,
		nuContrato,
		nmUsuario,
		nmSocialUsuario,
		statusBeneficiarioLogado,
		tipoBeneficiarioLogado,
		acessoSuspenso,
	} = plan;
	const IS_CANCELLED = statusBeneficiarioLogado === BeneficiarySituationEnum.CANCELLED;
	const IS_DEPENDENT = tipoBeneficiarioLogado === UserTypeEnum.DEPENDENT;
	const {palette, colors} = useTheme();
	const {data: beneficiariesList} = useGetBeneficiaries({nuContrato});

	const {isEnabled: isNewUIEntryFlowIsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_NEW_UI_ENTRY_FLOW,
	);

	const uniqueBeneficiaries = new Set();
	const filteredBeneficiaries = beneficiariesList
		? beneficiariesList?.filter(
				(beneficiary: BeneficiaryModel) =>
					!uniqueBeneficiaries.has(beneficiary.cdUsuarioC) &&
					uniqueBeneficiaries.add(beneficiary.cdUsuarioC),
			)
		: null;

	const isUniqueBeneficiary = filteredBeneficiaries?.length === 1;

	const isPlanCancelledDatesAreEqual = Boolean(
		filteredBeneficiaries?.every(
			(currentBeneficiary, _index, prevListBeneficiaries) =>
				currentBeneficiary?.dtCancelamentoC === prevListBeneficiaries[0]?.dtCancelamentoC,
		),
	);

	const handleSelect = (event: MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		if (onSelect) {
			onSelect({
				plan,
				beneficiary: isUniqueBeneficiary ? filteredBeneficiaries[0] : undefined,
			});
		}
	};

	const onlySupendendContract = acessoSuspenso && !IS_CANCELLED;
	const shouldHasBorder = isNewUIEntryFlowIsEnabled && IS_CANCELLED && !selected;

	return (
		<StyledCard
			id="plan-card"
			data-tag-id={SelectionUserPlanEnum.SELECIONOU_PLANO}
			elevation={selected ? 1 : 0}
			selected={selected}
			onClick={onSelect && handleSelect}
			sx={{
				border: shouldHasBorder ? `1px solid ${palette.divider}` : undefined,
				justifyContent: isNewUIEntryFlowIsEnabled ? 'space-between' : 'flex-start',
				gap: isNewUIEntryFlowIsEnabled ? 0.5 : 1,
			}}>
			<Box>
				{isNewUIEntryFlowIsEnabled ? (
					<Stack
						justifyContent="space-between"
						alignItems="center"
						direction="row"
						gap={2}
						mb={0.5}>
						<TagChip
							label={planTypeOptions[tipoPlanoC]?.label}
							color={planTypeOptions[tipoPlanoC]?.color}
							bgColor={
								tipoPlanoC === PlanTypeEnum.HEALTH
									? colors.light.primary['30Percent']
									: undefined
							}
							data-testid="plan-chip"
						/>

						{actionButton ?? (
							<Fragment>
								{selected ? (
									<Loading size={20} color="primary" fullWidth={false} />
								) : (
									<Icon
										name="arrow-right-filled"
										color={palette.primary.main}
										size={20}
									/>
								)}
							</Fragment>
						)}
					</Stack>
				) : null}

				<Box color={palette.primary.main}>
					<Typography variant="h6">{nmPlano}</Typography>
					<Typography variant="body2">
						{beneficiaryName || formatText(nmSocialUsuario || nmUsuario)}
					</Typography>
				</Box>
				{isNewUIEntryFlowIsEnabled ? null : <Divider sx={{my: 1}} />}
				<Stack direction="row">
					<ListItemText
						slotProps={{primary: {variant: 'body2'}}}
						primary={contactNumber}
						secondary={nuContrato}
						sx={{m: isNewUIEntryFlowIsEnabled ? '8px 0 0 0' : 0}}
					/>
					{IS_DEPENDENT && IS_CANCELLED ? null : (
						<ListItemText
							slotProps={{primary: {variant: 'body2'}}}
							secondary={
								Array.isArray(filteredBeneficiaries) ? (
									filteredBeneficiaries.length
								) : (
									<Skeleton width={20} />
								)
							}
							primary={beneficiaries}
							sx={{m: isNewUIEntryFlowIsEnabled ? '8px 0 0 0' : 0}}
						/>
					)}
				</Stack>
			</Box>
			{isNewUIEntryFlowIsEnabled ? (
				<Box>
					{IS_CANCELLED ? (
						<Stack direction="row" alignItems="center" gap={0.5} mt={1}>
							<Icon
								name="warning-outlined"
								color={palette.warning.main}
								size={14}
							/>
							<Typography variant="caption" color="text.primary">
								{isPlanCancelledDatesAreEqual ? cancelIn : cancelContract}
								<strong>{filteredBeneficiaries?.[0]?.dtCancelamentoC}</strong>
							</Typography>
						</Stack>
					) : null}
					{onlySupendendContract ? <AlertSuspended /> : null}
				</Box>
			) : (
				<Fragment>
					{IS_CANCELLED ? (
						<AlertCancelled
							dtCancelamento={
								isPlanCancelledDatesAreEqual
									? filteredBeneficiaries?.[0]?.dtCancelamentoC
									: null
							}
						/>
					) : null}
					{onlySupendendContract ? <AlertSuspended /> : null}
					<Stack
						justifyContent="space-between"
						alignItems="center"
						direction="row"
						gap={2}
						paddingTop={1}
						sx={{mt: 'auto'}}>
						<TagChip
							label={planTypeOptions[tipoPlanoC]?.label}
							color={planTypeOptions[tipoPlanoC]?.color}
							bgColor={
								tipoPlanoC === PlanTypeEnum.HEALTH
									? colors.light.primary['30Percent']
									: undefined
							}
							data-testid="plan-chip"
						/>
						{actionButton ?? (
							<StyledButton
								size="small"
								endIcon={selected && <Icon name="success" size={20} />}
								color={selected ? 'success' : 'primary'}
								variant="text"
								onClick={handleSelect}
								$selected={selected}>
								{selected ? selectedLabel : select}
							</StyledButton>
						)}
					</Stack>
				</Fragment>
			)}
		</StyledCard>
	);
}
